import React, { createContext, useState, useEffect, useContext, useSyncExternalStore } from "react";
import { websocketService } from "../services/websocketService";
import { getToken } from "../utils/authUtils";
import store from "../store";
import { BellNotificationContext } from "./BellNotificationContext";
import { NotificationContext } from "./NotificationContext";
import { EarthquakeContext } from "./EarthquakeContext";
import { RpmContext } from "./RpmContext";
import { FreqContext } from "./FreqContext";

export const GlobalContext = createContext();

// WebSocket 要帶 token 才連得上（後端以 4401 拒絕，2026-10-16），而 GlobalProvider 包住整個 app（含登入頁），
// 得知道「現在有沒有 token、換了沒」。token 存在 cookie，沒有變更事件可聽，所以：
//   - 訂閱 Redux store：登入後一進受保護頁面，App 就會 dispatch setUserDatas，登入幾乎立刻連上
//   - 每 5 秒重讀一次：登出（removeTokenCookie）不經過 Redux，靠這個在 5 秒內斷線
// 只有 token 字串真的變了才會觸發重新渲染與重連。
const TOKEN_CHECK_INTERVAL = 5000;
function subscribeTokenChange(onChange) {
	const unsubscribe = store.subscribe(onChange);
	const timer = setInterval(onChange, TOKEN_CHECK_INTERVAL);
	return () => {
		unsubscribe();
		clearInterval(timer);
	};
}
const readToken = () => getToken() || null;

export const GlobalProvider = ({ children }) => {
	const [isIntranetUsed, setIsIntranetUsed] = useState(false); //是否是內網

	// 取得dispatch
	const { dispatch: bellDispatch } = useContext(BellNotificationContext);
	const { dispatch: notificationDispatch } = useContext(NotificationContext);
	const { dispatch: earthquakeDispatch } = useContext(EarthquakeContext);
	const { dispatch: rpmDispatch } = useContext(RpmContext);
	const {dispatch: freqDispatch} = useContext(FreqContext);
	const token = useSyncExternalStore(subscribeTokenChange, readToken);

	useEffect(() => {
		setIsIntranetUsed(process.env.Intranet === "true");

		// 沒登入（登入頁、已登出）不連：連了也只會被後端以 4401 關掉
		if (!token) return;

		const baseWebsocketUrl = process.env.REACT_APP_WS_URL;
		// 瀏覽器的 WebSocket 不能帶 Authorization 標頭，token 只能放 query string（取捨見後端 wsAuth.js）
		const auth = `?token=${encodeURIComponent(token)}`;

		// 基本WebSocket連接
		const connections = [
			{
				key: 'notification-bell',
				url: `${baseWebsocketUrl}/ws/notification-bell${auth}`,
				handler: (msg) => {
					switch (msg.type) {
						case "INITIAL_BELLNOTIFICATIONS":
						case "UPDATE_BELLNOTIFICATIONS":
						case "MORE_BELLNOTIFICATIONS":
							bellDispatch({ type: msg.type, payload: msg.data });
							break;
					}
				}
			},
			{
				key: 'notification',
				url: `${baseWebsocketUrl}/ws/notification${auth}`,
				handler: (msg) => {
					switch (msg.type) {
						case "INITIAL_NOTIFICATIONS":
						case "UPDATE_NOTIFICATIONS":
						case "MORE_NOTIFICATIONS":
							notificationDispatch({ type: msg.type, payload: msg.data });
							break;
					}
				}
			},
			{
				key: 'freq',
				url: `${baseWebsocketUrl}/ws/freq${auth}`,
				handler: (msg) => {
					switch (msg.type) {
						case "INITIAL_FREQS":
						case "UPDATE_FREQS":
							freqDispatch({ type: msg.type, payload: msg.data });
							break;
					}
				}
			}
		];

		// RPM WebSocket (只在Pi/內網環境)
		if (process.env.Intranet === "true") {
			connections.push({
				key: 'rpm',
				url: `${baseWebsocketUrl}/ws/rpm${auth}`,
				handler: (msg) => {
					switch (msg.type) {
						case "INITIAL_RPMS":
						case "UPDATE_RPMS":
							rpmDispatch({ type: msg.type, payload: msg.data });
							break;
					}
				}
			});
		}

		// 地震數據WebSocket (只在非內網環境)
		if (process.env.Intranet === "false") {
			connections.push({
				key: 'earthquake',
				url: `${baseWebsocketUrl}/ws/earthquake${auth}`,
				handler: (msg) => {
					switch (msg.type) {
						case "INITIAL_EARTHQUAKES":
						case "UPDATE_EARTHQUAKES":
						case "MORE_EARTHQUAKES":
							earthquakeDispatch({ type: msg.type, payload: msg.data });
							break;
					}
				}
			});
		}

		// 建立所有連接
		connections.forEach(conn => {
			websocketService.connect(conn.url, conn.handler, conn.key);
		});

		return () => {
			websocketService.disconnectAll();
		};
	}, [
		// 移除dispatch依賴，避免不必要的重連
		// bellDispatch,
		// notificationDispatch,
		// earthquakeDispatch,
		// rpmDispatch,
		// freqDispatch,
		isIntranetUsed,
		// token 變了（登入、登出、換帳號、重新登入換發）就斷掉舊連線、用新 token 重連
		token,
	]);

	return (
		<GlobalContext.Provider
			value={{
				bellDispatch,
				notificationDispatch,
				earthquakeDispatch,
				rpmDispatch,
				freqDispatch,
				isIntranetUsed,
			}}
		>
			{children}
		</GlobalContext.Provider>
	);
};
